/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import appointmentBookingConfig from '../../Data/Maps/appointmentBookingConfig';
import PreloadStatus from '../../Data/PreloadStatus';
import { isAppointmentValid } from '../../Hooks/useNavigation/useNavigation.helpers';
import {
  setPreloadStatus,
  setSelectedAppointment,
  setShowAppointmentBooking,
  setWasPreviousPageAppointmentBooking,
} from '../../Lib/redux/slices/appointmentBooking';
import { updateRecalculateRequiredWidgetHeight } from '../../Lib/redux/slices/dynamicHeight';
import {
  setPendingMoveForwards,
  setShouldNavigateForward,
} from '../../Lib/redux/slices/navigationControls';
import { getEnvConfigForFormId } from '../__helpers__/component.helpers';
import { exposeUserActionEvent } from '../../Helpers/events';
import FetchAppointments from './FetchAppointments';
import {
  getEventDataFromIframeMessageEvent,
  getSelectedAppointmentEndDate,
  getSelectedClinicAddress,
  getSelectedClinicMigrationStatus,
  getSelectedClinicName,
  getSelectedClinicShopIdPRM,
  shouldShowAppointmentBookingOnPage,
} from './appointmentBooking.helpers';
import makeAppointmentBookingStyling from './makeAppointmentBookingStyling';
import { convertZeroToOneIndexIfNumber } from '../../Utils/utils';

const AppointmentBooking = () => {
  const appointmentBookingContainerRef = useRef();
  const appointmentTransitionContainerRef = useRef();
  const iframeRef = useRef();
  const dispatch = useDispatch();
  const { isDynamicHeightEnabled, iFrameId } = useSelector(
    (store) => store.optionalFormConfig
  );
  const [iFrameSelectedDate, setIFrameSelectedDate] = useState();
  const [selectionTimestamp, setSelectionTimestamp] = useState();
  const [iFrameSelectedLocationId, setIFrameSelectedLocationId] = useState();
  const [hasAppointmentPromptBeenReached, setHasAppointmentPromptBeenReached] =
    useState(false);
  const { formId, localeCode, countryCode } = useSelector(
    (store) => store.formConfig
  );
  const isBounceNavigationAnimationEnabled = useSelector(
    (store) => store.features.isBounceNavigationAnimationEnabled
  );
  const components = useSelector((store) => store.components);
  const { currentPage } = useSelector((store) => store.navigation);
  const {
    preloadStatus,
    showAppointmentBooking,
    selectedAppointment,
    wasPreviousPageAppointmentBooking,
    appointments,
    isNextPageAppointmentBooking,
  } = useSelector((store) => store.appointmentBooking);
  const { enterPhase, exitPhase } = useSelector((store) => store.transition);
  const configForFormId = getEnvConfigForFormId({
    config: appointmentBookingConfig,
    formId,
  });
  const styling = makeAppointmentBookingStyling({
    iframeLoaded: preloadStatus === PreloadStatus.COMPLETED,
    showContent: showAppointmentBooking,
  });
  const iframeUrl = configForFormId?.iframeUrl;

  const [shouldEnableMessageListener, setShouldEnableMessageListener] =
    useState(false);

  useEffect(() => {
    /**
     * send appointment to iframe when it has loaded and they are available
     */
    if (
      appointments &&
      iframeRef.current &&
      preloadStatus === PreloadStatus.COMPLETED
    ) {
      iframeRef.current.contentWindow.postMessage(
        {
          type: 'setAppointmentBookingData',
          appointments,
        },
        '*'
      );
    }
  }, [appointments, preloadStatus, iframeRef]);

  useEffect(() => {
    if (!configForFormId) {
      return;
    }

    const handlePreloadTimeout = () => {
      if (preloadStatus !== PreloadStatus.COMPLETED) {
        dispatch(setPendingMoveForwards({ isAwaitingAsyncRequest: false }));
        dispatch(
          setPreloadStatus({
            preloadStatus: PreloadStatus.NOT_STARTED,
          })
        );
      }
    };

    const handleIframeLoad = () => {
      dispatch(
        setPreloadStatus({
          preloadStatus: PreloadStatus.COMPLETED,
        })
      );
      clearTimeout(errorTimeout);
    };

    let errorTimeout;
    if (preloadStatus === PreloadStatus.WAITING_FOR_START) {
      dispatch(
        setPreloadStatus({
          preloadStatus: PreloadStatus.IN_PROGRESS,
        })
      );

      errorTimeout = setTimeout(handlePreloadTimeout, 7000);

      if (iframeRef.current) {
        iframeRef.current.src = iframeUrl;
        iframeRef.current.addEventListener('load', handleIframeLoad);
      }

      setShouldEnableMessageListener(true);
    }

    return () => {
      if (errorTimeout) {
        clearTimeout(errorTimeout);
      }
    };
  }, [preloadStatus]);

  const handleAppointmentSelection = (event) => {
    try {
      const eventData = getEventDataFromIframeMessageEvent(event);
      const { selectedDate, selectedLocationId, selectedLocationName } =
        eventData;
      setIFrameSelectedDate(selectedDate);
      setIFrameSelectedLocationId(selectedLocationId);
      setSelectionTimestamp(new Date()); // handle where the same details are selected

      exposeUserActionEvent({
        type: 'appointmentConfirmed',
        iFrameId,
        actionDetails: {
          appointment: {
            clinic: {
              id: selectedLocationId,
              name: selectedLocationName,
            },
            isoDate: selectedDate,
          },
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        'Error parsing JSON from Appointment Booking Iframe',
        error.message
      );
    }
  };

  const handleAppointmentRejection = () => {
    exposeUserActionEvent({
      type: 'skipClinicSelectionClicked',
      iFrameId,
    });

    dispatch(
      setSelectedAppointment({
        selectedDate: null,
        selectedEndDate: null,
        selectedLocation: null,
      })
    );
    setSelectionTimestamp(new Date()); // handle where the same details are selected
  };

  const handleClinicSelected = (event) => {
    const eventData = getEventDataFromIframeMessageEvent(event);

    exposeUserActionEvent({
      type: 'clinicSelected',
      iFrameId,
      actionDetails: {
        clinic: {
          id: eventData.selectedClinicId,
          name: eventData.selectedClinicName,
        },
      },
    });
  };

  const handleShowAppointmentsClicked = () => {
    exposeUserActionEvent({
      type: 'showAppointmentsClicked',
      iFrameId,
    });
  };

  const handleAppointmentTimeSelected = (event) => {
    const eventData = getEventDataFromIframeMessageEvent(event);
    const { selectedDate } = eventData;

    exposeUserActionEvent({
      type: 'appointmentTimeSelected',
      iFrameId,
      actionDetails: {
        appointment: {
          isoDate: selectedDate,
        },
      },
    });
  };

  const eventHandlers = {
    onAppointmentSelection: handleAppointmentSelection,
    onAppointmentRejection: handleAppointmentRejection,
    onShowAppointmentsClicked: handleShowAppointmentsClicked,
    onClinicSelected: handleClinicSelected,
    OnAppointmentTimeSelected: handleAppointmentTimeSelected,
  };

  const handleWindowMessage = (event) => {
    if (
      event.data.type === 'resize-appointment-booking-iframe' &&
      event.data.width &&
      event.data.height
    ) {
      handleResizeIframe(event.data.height);
    }

    if (typeof event.data.startsWith === 'function') {
      if (!appointmentBookingContainerRef.current) {
        return; // Exit early if component is unmounted
      }
      const eventNameMatch = event.data.match(/^[^:]*/);
      if (!eventNameMatch) return;

      const eventName = eventNameMatch[0];
      const eventHandler = eventHandlers[eventName];
      if (eventHandler) {
        eventHandler(event);
      }
    }
  };

  const handleResizeIframe = (height) => {
    if (iframeRef.current) {
      iframeRef.current.style.height = `${height}px`;

      if (isDynamicHeightEnabled && height > 0) {
        dispatch(
          updateRecalculateRequiredWidgetHeight({
            isWidgetHeightRecalculationRequired: true,
          })
        );
      }
    }
  };

  useEffect(() => {
    // This useEffect hook prevents the addition of multiple event listeners for the same function.
    if (shouldEnableMessageListener) {
      window.addEventListener('message', handleWindowMessage);
    }
    return () => {
      window.removeEventListener('message', handleWindowMessage);
    };
  }, [shouldEnableMessageListener]);

  useEffect(() => {
    if (!iFrameSelectedDate || !iFrameSelectedLocationId || !appointments) {
      return;
    }

    const selectedLocationName = getSelectedClinicName({
      appointments,
      selectedLocationId: iFrameSelectedLocationId,
    });

    const selectedLocationAddress = getSelectedClinicAddress({
      appointments,
      selectedLocationId: iFrameSelectedLocationId,
    });

    const selectedLocationIsMigrated = getSelectedClinicMigrationStatus({
      appointments,
      selectedLocationId: iFrameSelectedLocationId,
    });

    const selectedLocationShopIdPRM = getSelectedClinicShopIdPRM({
      appointments,
      selectedLocationId: iFrameSelectedLocationId,
    });

    const selectedAppointmentEndDate = getSelectedAppointmentEndDate({
      appointments,
      selectedLocationId: iFrameSelectedLocationId,
      selectedDate: iFrameSelectedDate,
    });

    const selectedLocation = {
      id: iFrameSelectedLocationId,
      name: selectedLocationName,
      address: selectedLocationAddress,
      locale: localeCode, // This needs to be the locale code of the form i.e. "de_DE", "en_US
      countryAlpha2: countryCode, // This needs to be a two digit alpha country code for the API requests in the gateway, thankfully the countryCode of the form is already in this format
      isMigrated: selectedLocationIsMigrated,
      shopIdPRM: selectedLocationShopIdPRM,
    };

    dispatch(
      setSelectedAppointment({
        selectedDate: iFrameSelectedDate,
        selectedLocation,
        selectedEndDate: selectedAppointmentEndDate, // if one exists or default
      })
    );
  }, [
    iFrameSelectedDate,
    iFrameSelectedLocationId,
    appointments,
    localeCode,
    countryCode,
    dispatch,
  ]);

  useEffect(() => {
    if (!configForFormId) {
      return;
    }
    /**
     * This is a cleanup effect that is called when the component is unmounted.
     * It resets the statuses so that it can load again when needed
     */
    return () => {
      dispatch(
        setPreloadStatus({
          preloadStatus: PreloadStatus.NOT_STARTED,
        })
      );
      dispatch(
        setShowAppointmentBooking({
          showAppointmentBooking: false,
        })
      );
    };
  }, []);

  useEffect(() => {
    if (!configForFormId) {
      return;
    }

    if (enterPhase === true) {
      /**
       * Enter phase has just begun (i.e. enterPhase has just become true)
       * If we are about to enter the appointment booking page, we need to check if we should show the appointment booking widget
       */
      const shouldShowAppointmentBookingOnThisPageResult =
        shouldShowAppointmentBookingOnPage({
          components,
          formId,
          pageNumber: currentPage,
        });

      let transitionAnimationBaseClassPrefix;
      if (isBounceNavigationAnimationEnabled) {
        transitionAnimationBaseClassPrefix =
          'bounce-appointment-booking-direction';
      } else {
        transitionAnimationBaseClassPrefix =
          'transition-appointment-booking-direction';
      }

      if (shouldShowAppointmentBookingOnThisPageResult) {
        appointmentTransitionContainerRef.current.classList.add(
          `${transitionAnimationBaseClassPrefix}-enter-active`
        );
        appointmentTransitionContainerRef.current.classList.remove(
          `${transitionAnimationBaseClassPrefix}-exit-active`
        );
      }

      dispatch(
        setShowAppointmentBooking({
          showAppointmentBooking: shouldShowAppointmentBookingOnThisPageResult,
        })
      );
    }
    if (enterPhase === false) {
      /**
       * Enter phase has just completed (i.e. enterPhase has just become true)
       * If we have just finished entering the appointment booking page, we need to send out
       * the 'appointmentPromptReached' userAction event to the parentPage.
       */
      if (
        showAppointmentBooking === true &&
        hasAppointmentPromptBeenReached === false
      ) {
        setHasAppointmentPromptBeenReached(true);

        exposeUserActionEvent({
          type: 'appointmentPromptReached',
          iFrameId,
          actionDetails: {
            page: convertZeroToOneIndexIfNumber(currentPage),
          },
        });
      }
    }

    if (
      exitPhase === false &&
      showAppointmentBooking === true &&
      isDynamicHeightEnabled === true
    ) {
      /**
       * If we have just exited the appointment booking page, we need to recalculate the widget height
       */
      dispatch(
        updateRecalculateRequiredWidgetHeight({
          isWidgetHeightRecalculationRequired: true,
        })
      );
    }
  }, [enterPhase]);

  useEffect(() => {
    if (!configForFormId) {
      return;
    }

    if (exitPhase === true) {
      /**
       * If we are exiting the appointmentBooking page set wasPreviousPageAppointmentBooking and add the exit transition class so the transition of this iframe is smooth
       */
      if (
        showAppointmentBooking === true &&
        wasPreviousPageAppointmentBooking !== true
      ) {
        dispatch(
          setWasPreviousPageAppointmentBooking({
            wasPreviousPageAppointmentBooking: true,
          })
        );
      } else if (wasPreviousPageAppointmentBooking !== false) {
        dispatch(
          setWasPreviousPageAppointmentBooking({
            wasPreviousPageAppointmentBooking: false,
          })
        );
      }

      let transitionAnimationBaseClassPrefix;
      if (isBounceNavigationAnimationEnabled) {
        transitionAnimationBaseClassPrefix =
          'bounce-appointment-booking-direction';
      } else {
        transitionAnimationBaseClassPrefix =
          'transition-appointment-booking-direction';
      }

      appointmentTransitionContainerRef.current.classList.add(
        `${transitionAnimationBaseClassPrefix}-exit-active`
      );
      appointmentTransitionContainerRef.current.classList.remove(
        `${transitionAnimationBaseClassPrefix}-enter-active`
      );
    }

    if (exitPhase === false && wasPreviousPageAppointmentBooking === true) {
      // We are exiting the Page with AppointmentBooking on it by the user navigating backwards.
      // Set the status back to NOT_STARTED so it can load again next time
      // This guards against a user navigating backwards and forwards due to a networking error,
      // which would mean the user doesn't get stuck with broken styles
      dispatch(
        setPreloadStatus({
          preloadStatus: PreloadStatus.NOT_STARTED,
        })
      );
    }
  }, [exitPhase]);

  useEffect(() => {
    /**
     * Handle: navigation on selection / rejection in the iframe
     */
    if (!configForFormId || !selectionTimestamp) {
      return;
    }

    const wasAppointmentRejected =
      selectedAppointment?.selectedDate === null ||
      selectedAppointment?.selectedLocation === null;

    // if we have had a valid response from the iframe
    const isSelectedAppointmentValid = isAppointmentValid({
      appointment: selectedAppointment,
    });

    if (isSelectedAppointmentValid || wasAppointmentRejected) {
      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
    }
  }, [selectedAppointment, configForFormId, dispatch, selectionTimestamp]);

  useEffect(() => {
    /**
     * Handle: on first render, if the preload status is completed then the iframe has already been loaded, and the iframe will need to be reloaded. This is because the iframe is unmounted and remounted when the user navigates backwards and forwards.
     */
    if (!configForFormId) {
      return;
    }
    if (preloadStatus === PreloadStatus.COMPLETED) {
      // Unmounts and remounts are causing the ref to be null, so re-apply the card URL if this happens
      dispatch(
        setPreloadStatus({
          preloadStatus: PreloadStatus.NOT_STARTED,
        })
      );
    }
  }, []);

  if (!configForFormId) {
    return null;
  }

  let appointmentBookingPage;
  if (isNextPageAppointmentBooking) {
    appointmentBookingPage = currentPage + 1;
  }

  return (
    <div
      ref={appointmentTransitionContainerRef}
      className="appointmentBookingTransitionContainer"
    >
      <FetchAppointments
        configForFormId={configForFormId}
        components={components}
        isNextPageAppointmentBooking={isNextPageAppointmentBooking}
        appointments={appointments}
        preloadStatus={preloadStatus}
        appointmentBookingPage={appointmentBookingPage}
      />
      <Grid css={styling.grid} container direction="column">
        <Container
          css={styling.container}
          maxWidth="md"
          ref={appointmentBookingContainerRef}
          data-testid="appointmentBookingContentContainer"
        >
          <div css={styling.iframeContainer}>
            <iframe
              data-testid="appointmentBookingIframe"
              css={styling.iframe}
              title="Appointment Booking"
              ref={iframeRef}
              sandbox="allow-top-navigation allow-scripts allow-same-origin"
            />
          </div>
        </Container>
      </Grid>
    </div>
  );
};

export default AppointmentBooking;
